// components/shared/FilterBar/FilterBar.tsx
import { useState, useRef, useEffect } from "react";
import { FilterFieldConfig } from "types/FilterFieldConfig";
import { FiFilter, FiX } from "react-icons/fi";

export function FilterBar<T extends Record<string, any>>({
  filters,
  fields,
  onChange,
  ignoreActiveKeys = [],
}: {
  filters: T;
  fields: FilterFieldConfig<T>[];
  onChange: (key: keyof T, value: any) => void;
  ignoreActiveKeys?: (keyof T)[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeCount = Object.keys(filters).filter((key) => {
    if (ignoreActiveKeys.indexOf(key as keyof T) !== -1) return false;
    const value = filters[key as keyof T];
    if (value === undefined || value === null || value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }).length;

  const handleClearAll = () => {
    fields.forEach((field) => {
      onChange(field.key, undefined);
    });
  };

  return (
    <div className="position-relative d-inline-block" ref={dropdownRef}>
      <button
        className="btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          height: "42px",
          border: "1px solid #e0e0e0",
          backgroundColor: "#f8f9fa",
          color: "#333",
          transition: "background-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f1f3f5";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
      >
        <FiFilter size={18} className="text-muted" />
        <span className="fw-medium" style={{ color: "#333" }}>
          Bộ lọc
        </span>
        {activeCount > 0 && (
          <span className="badge bg-primary rounded-pill px-2 py-1">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show p-4 shadow-lg border-0"
          style={{
            minWidth: "320px",
            maxWidth: "480px",
            right: 0,
            left: "auto",
            borderRadius: "16px",
            marginTop: "12px",
            background: "#ffffff",
            animation: "fadeInDown 0.2s ease",
            maxHeight: "80vh",
            overflowY: "auto",
            zIndex: 1050,
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0 fw-bold">Bộ lọc</h6>
            {activeCount > 0 && (
              <button
                className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                onClick={handleClearAll}
              >
                <FiX size={14} />
                Xoá hết
              </button>
            )}
          </div>

          <div className="d-flex flex-column gap-3">
            {fields.map((field) => {
              const value = filters[field.key];
              return (
                <div key={field.key}>
                  <label className="form-label small text-secondary fw-semibold mb-1">
                    {field.label}
                  </label>

                  {field.type === "text" && (
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={field.placeholder || "Search..."}
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(field.key, e.target.value || undefined)
                      }
                    />
                  )}

                  {field.type === "number" && (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(
                          field.key,
                          e.target.value === ""
                            ? undefined
                            : Number(e.target.value),
                        )
                      }
                    />
                  )}

                  {field.type === "date" && (
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(field.key, e.target.value || undefined)
                      }
                    />
                  )}

                  {field.type === "select" && (
                    <select
                      className="form-select form-select-sm"
                      value={value?.toString() ?? ""}
                      onChange={(e) =>
                        onChange(
                          field.key,
                          e.target.value === "" ? undefined : e.target.value,
                        )
                      }
                    >
                      {!field.hideAllOption && <option value="">Tất cả</option>}
                      {field.options?.map((opt) => (
                        <option key={opt.value} value={opt.value.toString()}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.type === "boolean" && (
                    <select
                      className="form-select form-select-sm"
                      value={
                        value === true ? "true" : value === false ? "false" : ""
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        onChange(
                          field.key,
                          v === "" ? undefined : v === "true",
                        );
                      }}
                    >
                      <option value="">Tất cả</option>
                      <option value="true">Có</option>
                      <option value="false">Không</option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}
